import { flexRender, Row, RowData, type Table as TsTable } from '@tanstack/react-table';
import { ForwardedRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAccount } from 'wagmi';
import { ProductPrice } from '../../hooks/useProductPricesSub';
import { cn } from '../../lib/classNameHelpers';
import { SortIcon } from './SortIcon';
import {
  SkeletonRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeadRow,
  TableRow,
} from './TableCore';

declare module '@tanstack/table-core' {
  interface TableMeta<TData extends RowData> {
    getRowClassName?: (row: Row<TData>) => string;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellHeaderContainerClassName?: string;
    cellHeaderClassname?: string;
    cellContainerClassName?: string;
    cellClassname?: string;
    align?: 'left' | 'right';
  }
}
export type MarketTableData = {
  market: string;
  productId: string;
  routingTicker: string;
  volume24h: bigint | undefined;
  fundingRate1h: bigint;
  baseTokenName: string;
  maxLeverage: number;
  isFavorite: boolean;
  price: ProductPrice | undefined;
};

export type MarketStateTableProps<T> = {
  table: TsTable<T>;
  isPending: boolean;
  noDataText?: string;
  skeletonRows?: number;
  isFetching?: boolean;
  onRowClick: ({ routingTicker }: { routingTicker: string }) => void;
  tableHeaderClassname?: string;
  skeletonRowClassName?: string;
  requireConnected?: boolean;
  setSelectedRowIndex: (value: React.SetStateAction<number>) => void;
  selectedRowIndex: number;
  isUsingKeyboard: boolean;
  onRowFocus: (index: number) => void;
};

export const MarketStateTableInner = <T,>(
  {
    table,
    isPending,
    isFetching,
    noDataText = 'No data available',
    skeletonRows = 4,
    onRowClick,
    tableHeaderClassname,
    skeletonRowClassName,
    requireConnected = true,
    setSelectedRowIndex,
    selectedRowIndex,
    isUsingKeyboard,
    onRowFocus,
  }: MarketStateTableProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const { isConnected } = useAccount();
  const originalRows = table.getSortedRowModel().rows;

  const rows = originalRows.sort((a, b) => {
    const aIsFavorite = (a.original as MarketTableData).isFavorite;
    const bIsFavorite = (b.original as MarketTableData).isFavorite;
    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;
    return 0;
  });

  const columns = table.getAllColumns();

  const renderTableContent = () => {
    if (!isConnected && requireConnected) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length}
            className="h-40 py-4 text-center text-contentSecondary"
          >
            {'Connect your wallet to deposit funds & start trading'}
          </TableCell>
        </TableRow>
      );
    }
    // If we're pending and not fetching we should show no data, as the query is disabled and not loading.
    if (isPending && isFetching) {
      return Array.from({ length: skeletonRows }).map((_, rowIndex) => (
        <SkeletonRow
          columnCount={columns.length}
          key={`skeleton-row-${rowIndex}`}
          skeletonRowClassName={skeletonRowClassName}
        />
      ));
    }

    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} className="py-4 text-contentSecondary">
            {noDataText}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row, rowIdx) => (
      <TableRow
        className={cn(
          'border-none',
          rowIdx === selectedRowIndex && '!bg-backgroundHoverOverlay',
          'focus-visible:relative focus-visible:outline-none',
          'focus-visible:after:pointer-events-none focus-visible:after:absolute focus-visible:after:inset-0',
          'focus-visible:after:rounded focus-visible:after:border-2 focus-visible:after:border-blue-300'
        )}
        onClick={onRowClick ? () => onRowClick(row.original as MarketTableData) : undefined}
        onMouseEnter={() => {
          if (!isUsingKeyboard && setSelectedRowIndex) {
            setSelectedRowIndex(rowIdx);
          }
        }}
        tabIndex={0}
        onFocus={() => onRowFocus(rowIdx)}
        key={row.id}
      >
        {row.getVisibleCells().map((cell) => {
          const align = cell.column.columnDef.meta?.align || 'left';
          return (
            <TableCell
              key={cell.id}
              className={twMerge(
                'text-nowrap text-contentPrimary',
                align === 'right' ? 'text-right' : 'text-left',
                cell.column.columnDef.meta?.cellContainerClassName
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  return (
    <Table ref={ref}>
      <TableHeader className={tableHeaderClassname}>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeadRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const align = header.column.columnDef.meta?.align || 'left';

              const sortIcon = (() => {
                if (!header.column.getCanSort()) {
                  return null;
                }

                const sortDirection = header.column.getIsSorted() || undefined;
                return <SortIcon sortDirection={sortDirection} />;
              })();

              return (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className={twMerge(
                    align === 'right' ? 'text-right' : 'text-left',
                    header.column.columnDef.meta?.cellHeaderContainerClassName
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className={twMerge(
                        'group flex items-center gap-1 text-contentSecondary hover:brightness-125 focus:brightness-125',
                        align === 'right' ? 'justify-end' : '',
                        header.column.columnDef.meta?.cellHeaderClassname,
                        header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        header.column.getCanSort()
                          ? header.column.getNextSortingOrder() === 'asc'
                            ? 'Click to sort ascending'
                            : header.column.getNextSortingOrder() === 'desc'
                              ? 'Click to sort descending'
                              : 'Clear sort'
                          : undefined
                      }
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortIcon}
                    </div>
                  )}
                </TableHead>
              );
            })}
          </TableHeadRow>
        ))}
      </TableHeader>
      <TableBody>
        {/* Spacer row to as margin between the header and body */}
        <tr className="h-4" />
        {renderTableContent()}
      </TableBody>
    </Table>
  );
};

export const MarketStateTable = forwardRef(MarketStateTableInner) as <T>(
  props: MarketStateTableProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => React.JSX.Element;
